import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Plus, Edit, Trash2, LogOut, Home } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';
import axios from 'axios';
import ProjectForm from '../components/ProjectForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEmailMenu, setShowEmailMenu] = useState(false);

  useEffect(() => {
    checkAuth();
    fetchProjects();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/projects`,
        getAuthHeaders()
      );
      setProjects(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin');
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Tem certeza que deseja excluir este projeto?')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/projects/${projectId}`,
        getAuthHeaders()
      );
      fetchProjects();
    } catch (error) {
      alert('Erro ao excluir projeto');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingProject(null);
    fetchProjects();
  };

  const formatPrice = (price) => {
    return price.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  if (showForm) {
    return (
      <ProjectForm
        project={editingProject}
        onClose={handleFormClose}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Painel Administrativo</h1>
              <p className="text-sm text-gray-600">ArqessenceShop</p>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/financeiro')}
                className="flex items-center space-x-2 hover:border-[#00A86B] hover:text-[#00A86B]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                <span>Financeiro</span>
              </Button>
              <div 
                className="relative"
                onMouseEnter={() => setShowEmailMenu(true)}
                onMouseLeave={() => setShowEmailMenu(false)}
              >
                <Button
                  variant="outline"
                  className="flex items-center space-x-2 hover:border-[#00A86B] hover:text-[#00A86B]"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>
                  <span>Email</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </Button>
                {showEmailMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
                    <button
                      onClick={() => {
                        setShowEmailMenu(false);
                        navigate('/admin/email/config');
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Configurações do SMTP
                    </button>
                    <button
                      onClick={() => {
                        setShowEmailMenu(false);
                        navigate('/admin/email/templates');
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Templates de Email
                    </button>
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/settings')}
                className="flex items-center space-x-2 hover:border-[#00A86B] hover:text-[#00A86B]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
                <span>Configurações</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/files')}
                className="flex items-center space-x-2 hover:border-[#00A86B] hover:text-[#00A86B]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7V4a2 2 0 0 1 2-2h8.5L20 7.5V20a2 2 0 0 1-2 2h-6"/><polyline points="14 2 14 8 20 8"/><path d="M5 17a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/><path d="M7 16.5 8 22l-3-1-3 1 1-5.5"/></svg>
                <span>Arquivos</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <Home size={18} />
                <span>Ir para o site</span>
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="flex items-center space-x-2 text-red-600 border-red-300 hover:bg-red-50"
              >
                <LogOut size={18} />
                <span>Sair</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Actions */}
        <div className="mb-6">
          <Button
            onClick={() => setShowForm(true)}
            className="bg-[#00A86B] hover:bg-[#008c5a] text-white flex items-center space-x-2 shadow-lg"
          >
            <Plus size={20} />
            <span>Novo Projeto</span>
          </Button>
        </div>

        {/* Projects List */}
        {loading ? (
          <div className="text-center py-16">
            <p className="text-gray-600">Carregando projetos...</p>
          </div>
        ) : projects.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-600 mb-4">Nenhum projeto cadastrado ainda</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-[#00A86B] hover:bg-[#008c5a] text-white"
            >
              Criar primeiro projeto
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex gap-6">
                  <img
                    src={project.images && project.images.length > 0 ? getImageUrl(project.images[0]) : project.image}
                    alt={project.name}
                    className="w-48 h-32 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">{project.name}</h3>
                        <div className="flex space-x-2 mt-2">
                          {project.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleEdit(project)}
                          className="hover:bg-blue-50 hover:text-blue-600"
                        >
                          <Edit size={18} />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleDelete(project.id)}
                          className="hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-600">Terreno</p>
                        <p className="font-semibold">{project.terrain}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Área</p>
                        <p className="font-semibold">{project.area}m²</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Quartos</p>
                        <p className="font-semibold">{project.bedrooms}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Preço</p>
                        <p className="font-semibold text-[#00A86B]">R$ {formatPrice(project.price)}</p>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm line-clamp-2">{project.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;